import { Check, X as XIcon } from "lucide-react";

import type {
  ContestConsents,
  ContestSubmissionDetail,
} from "@/types/contest-submission";

import { FieldNoteEditor } from "./field-note-editor";
import { formatBirthdate, formatDateTime, formatSubmittedAt } from "./format";
import { SubmitterSubmissions } from "./submitter-submissions";

/** 필드 메모를 붙이는 작품 본문 5개 필드 — 서버 field 값과 일치. */
const NOTE_FIELDS = [
  { field: "synopsis", label: "시놉시스" },
  { field: "summary", label: "줄거리" },
  { field: "logline", label: "로그라인" },
  { field: "setting", label: "주요 설정" },
  { field: "characters", label: "등장인물 소개" },
] as const;

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[130px_1fr] gap-3 py-1.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm break-words">{value || "-"}</span>
    </div>
  );
}

/**
 * 작품 정보 본문 — 크게·볼드 라벨 + 읽기 좋은 본문 (피드백 반영).
 * max-w-[720px]: 와이드 화면에서 줄이 늘어지지 않게 원고 뷰어(A4, 720px)와
 * 동일한 읽기 폭으로 제한 (한글 장문 적정 줄 길이 ~40자).
 * break-keep: 한글 단어 중간 줄바꿈 방지 (word-break: keep-all).
 */
function Block({ label, body }: { label: string; body: string | null }) {
  return (
    <div className="max-w-[720px] space-y-2">
      <h4 className="text-sm font-bold text-foreground">{label}</h4>
      <div className="rounded-md border bg-muted/30 px-5 py-4 text-base leading-7 whitespace-pre-wrap break-keep">
        {body || "-"}
      </div>
    </div>
  );
}

function ConsentsView({ consents }: { consents: ContestConsents | null }) {
  if (!consents) {
    return <p className="text-sm text-muted-foreground">동의 정보가 없습니다.</p>;
  }
  const rows: Array<[string, boolean]> = [
    ["개인정보 수집·이용 동의", consents.privacy],
    ["공모전 응모 약관 동의", consents.terms],
    ["표절·중복투고 금지 서약", consents.pledge],
  ];
  return (
    <div className="space-y-1.5">
      {rows.map(([label, ok]) => (
        <div key={label} className="flex items-center gap-2 text-sm">
          {ok ? (
            <Check className="h-4 w-4 text-emerald-600" />
          ) : (
            <XIcon className="h-4 w-4 text-rose-600" />
          )}
          <span>{label}</span>
        </div>
      ))}
      <p className="text-xs text-muted-foreground pt-1">
        동의 일시 · {formatDateTime(consents.consentedAt)} (버전 {consents.version})
      </p>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3 rounded-md border bg-white p-4 md:p-5">
      <h3 className="text-lg font-bold">{title}</h3>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

/** 드로어에서 포팅한 응모 정보 섹션들 — 전체화면 폭에 맞춰 타이포 확대. */
export function SubmissionInfoSections({
  detail,
}: {
  detail: ContestSubmissionDetail;
}) {
  return (
    <div className="space-y-4">
      <Section title="작품 정보">
        <Row label="작품 제목" value={detail.workTitle} />
        <Row label="키워드" value={detail.keywords} />
        <div className="grid grid-cols-1 gap-6 pt-1">
          {NOTE_FIELDS.map(({ field, label }) => (
            <div
              key={`${detail._id}:${field}`}
              className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,720px)_minmax(260px,380px)] xl:items-start"
            >
              <Block label={label} body={detail[field]} />
              {/* 본문 옆 메모 — xl 미만에서는 본문 아래로 내려온다 */}
              <div className="xl:pt-7">
                <FieldNoteEditor
                  submissionId={detail._id}
                  field={field}
                  saved={
                    detail.reviewNotes?.find((n) => n.field === field) ?? null
                  }
                />
              </div>
            </div>
          ))}
        </div>
      </Section>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Section title="기본 정보">
          <Row label="성명" value={detail.name} />
          <Row label="필명" value={detail.penName} />
          <Row label="생년월일" value={formatBirthdate(detail.birthdate)} />
          <Row label="이메일" value={detail.email} />
          <Row
            label="계정 이메일"
            value={
              detail.userEmail === detail.email ? (
                <span className="text-muted-foreground">
                  {detail.userEmail} (응모자 이메일과 동일)
                </span>
              ) : (
                detail.userEmail
              )
            }
          />
          <Row label="계정 이름" value={detail.userName} />
        </Section>

        <Section title="연락처">
          <Row label="휴대전화" value={detail.phone} />
          <Row label="비상연락망" value={detail.emergencyPhone} />
          <Row
            label="SNS/포트폴리오"
            value={
              detail.snsUrl ? (
                <a
                  href={detail.snsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline underline-offset-2"
                >
                  {detail.snsUrl}
                </a>
              ) : null
            }
          />
        </Section>
      </div>

      <Section title="이 응모자의 응모작">
        <SubmitterSubmissions
          userId={detail.userId}
          currentSubmissionId={detail._id}
        />
      </Section>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Section title="약관 동의 내역">
          <ConsentsView consents={detail.consents} />
        </Section>

        <Section title="메타">
          <Row label="제출 일시" value={formatSubmittedAt(detail)} />
          <Row label="마지막 수정" value={formatDateTime(detail.updatedAt)} />
          <Row
            label="응모자 ID"
            value={<code className="text-xs">{detail.userId}</code>}
          />
          <Row
            label="제출 ID"
            value={<code className="text-xs">{detail._id}</code>}
          />
        </Section>
      </div>
    </div>
  );
}
